import React, { useState, useMemo, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import { getArticulosGlobal } from "../api"
import {
  formatCurrency, formatNumber, formatCantidad, formatPercent,
  claseABC, rotacionConfig, tendenciaConfig, alertaConfig,
} from "../helpers"

const PAGE_SIZE = 100

const ORDER_OPTIONS = [
  { value: "importe_total", label: "Importe (mayor)" },
  { value: "cantidad_total", label: "Cantidad" },
  { value: "facturas", label: "# Facturas" },
  { value: "clientes", label: "# Clientes" },
  { value: "dias_con_ventas", label: "Días con ventas" },
  { value: "tasa_iva", label: "Tasa IVA" },
  { value: "descripcion", label: "Descripción A-Z" },
]

const cellHead = {
  padding: "10px 12px", fontSize: 11, fontWeight: 600,
  color: "#475569", textTransform: "uppercase", letterSpacing: 0.4,
  borderBottom: "1px solid #e2e8f0", background: "#f1f5f9",
  position: "sticky", top: 0, zIndex: 1, whiteSpace: "nowrap",
}

const Badge = ({ bg, color, children, title }) => (
  <span title={title} style={{
    display: "inline-block", padding: "2px 8px", borderRadius: 999,
    background: bg, color, fontSize: 11, fontWeight: 700,
  }}>{children}</span>
)

Badge.propTypes = { bg: PropTypes.string, color: PropTypes.string, children: PropTypes.node, title: PropTypes.string }

const FilaProducto = React.memo(({ producto, onVerDetalle }) => {
  const abc = claseABC(producto.abc?.clase)
  const rot = rotacionConfig(producto.movilidad?.rotacion)
  const ten = tendenciaConfig(producto.movilidad?.tendencia)
  const alertas = producto.alertas || []
  return (
    <tr style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer" }} onClick={() => onVerDetalle(producto)}>
      <td style={{ padding: "10px 12px", textAlign: "center", color: "#94a3b8", fontWeight: 700 }}>
        #{producto.ranking}
      </td>
      <td style={{ padding: "10px 12px" }}>
        <div style={{ fontWeight: 600, color: "#0f172a" }}>
          {producto.descripcion || "—"}
        </div>
        <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "ui-monospace, SFMono-Regular, monospace", marginTop: 2 }}>
          ID: {producto.id_producto}
        </div>
      </td>
      <td style={{ padding: "10px 12px", textAlign: "center" }}>
        <Badge bg={abc.bg} color={abc.color} title={abc.desc}>{abc.label}</Badge>
      </td>
      <td style={{ padding: "10px 12px", textAlign: "right", color: "#0f172a", fontWeight: 700 }}>
        {formatCurrency(producto.financiero?.importe_neto)}
        <div style={{ fontSize: 11, color: "#64748b", fontWeight: 400 }}>
          {formatPercent(producto.financiero?.participacion_pct)} del total
        </div>
      </td>
      <td style={{ padding: "10px 12px", textAlign: "right", color: "#475569" }}>
        {formatCantidad(producto.cantidades?.neta)}
        <div style={{ fontSize: 11, color: "#94a3b8" }}>
          neta
        </div>
      </td>
      <td style={{ padding: "10px 12px", textAlign: "center" }}>
        <Badge bg={rot.bg} color={rot.color}>
          <i className={`mdi mdi-${rot.icon} me-1`} />{rot.label}
        </Badge>
      </td>
      <td style={{ padding: "10px 12px", textAlign: "center" }}>
        <span style={{ color: ten.color, fontSize: 12, fontWeight: 700 }}>
          <i className={`mdi mdi-${ten.icon} me-1`} />{ten.label}
        </span>
      </td>
      <td style={{ padding: "10px 12px", textAlign: "right", color: "#475569" }}>
        {formatPercent(producto.movilidad?.penetracion_temporal_pct)}
        <div style={{ fontSize: 11, color: "#94a3b8" }}>
          {producto.movilidad?.dias_con_ventas}/{producto.movilidad?.dias_periodo} días
        </div>
      </td>
      <td style={{ padding: "10px 12px", textAlign: "right", color: "#475569" }}>
        {formatNumber(producto.distribucion?.sucursales_vendido)}/{formatNumber(producto.distribucion?.sucursales_total_periodo)}
      </td>
      <td style={{ padding: "10px 12px", textAlign: "right", color: "#475569" }}>
        {formatNumber(producto.distribucion?.clientes_unicos)}
      </td>
      <td style={{ padding: "10px 12px" }}>
        {alertas.length === 0 ? (
          <i className="mdi mdi-check-circle text-success" title="Sin alertas" />
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {alertas.slice(0, 2).map((a, i) => {
              const cfg = alertaConfig(a.tipo || a)
              return (
                <span key={i} title={cfg.label}
                  style={{ background: cfg.bg, color: cfg.color, padding: "1px 6px", borderRadius: 4, fontSize: 11 }}>
                  <i className={`mdi mdi-${cfg.icon}`} />
                </span>
              )
            })}
            {alertas.length > 2 && (
              <span style={{ fontSize: 10, color: "#64748b" }}>+{alertas.length - 2}</span>
            )}
          </div>
        )}
      </td>
    </tr>
  )
})

FilaProducto.propTypes = { producto: PropTypes.object, onVerDetalle: PropTypes.func }

const ProductosTab = ({ filtros, onVerDetalle }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [page, setPage] = useState(0)
  const [orderBy, setOrderBy] = useState("importe_total")
  const [orderDir, setOrderDir] = useState("desc")
  const [busqueda, setBusqueda] = useState("")
  const [debouncedBusqueda, setDebouncedBusqueda] = useState("")

  // Debounce de búsqueda
  useEffect(() => {
    const t = setTimeout(() => setDebouncedBusqueda(busqueda), 300)
    return () => clearTimeout(t)
  }, [busqueda])

  const cargar = useCallback(async () => {
    if (!filtros?.nitemisor) return
    setLoading(true)
    setError("")
    try {
      const { data: response } = await getArticulosGlobal({
        ...filtros,
        order_by: orderBy,
        order_dir: orderDir,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      })
      setData(response)
    } catch (err) {
      setError(err.response?.data?.error || err.message || "No se pudo cargar el listado.")
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [filtros, orderBy, orderDir, page])

  useEffect(() => { cargar() }, [cargar])

  // Reset página al cambiar orden
  useEffect(() => { setPage(0) }, [orderBy, orderDir])

  const productos = data?.data || data?.articulos || []
  const total = data?.total || data?.metadata?.total_articulos_disponibles || productos.length
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const filtrados = useMemo(() => {
    const q = debouncedBusqueda.toLowerCase()
    if (!q) return productos
    return productos.filter(p =>
      String(p.descripcion || "").toLowerCase().includes(q) ||
      String(p.id_producto || "").toLowerCase().includes(q),
    )
  }, [productos, debouncedBusqueda])

  return (
    <div style={{ background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0" }}>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
          <i className="mdi mdi-package-variant me-2 text-primary" />
          Catálogo ({formatNumber(filtrados.length)} de {formatNumber(productos.length)} en página · {formatNumber(total)} totales)
        </h6>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div style={{ position: "relative", maxWidth: 280 }}>
            <i className="mdi mdi-magnify" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
            <input
              type="text" className="form-control form-control-sm"
              placeholder="Buscar producto…"
              value={busqueda} onChange={e => setBusqueda(e.target.value)}
              style={{ paddingLeft: 32, minWidth: 200 }}
            />
          </div>
          <select
            className="form-select form-select-sm"
            style={{ minWidth: 180, fontSize: 13 }}
            value={orderBy}
            onChange={e => setOrderBy(e.target.value)}
          >
            {ORDER_OPTIONS.map(o => <option key={o.value} value={o.value}>Ordenar por: {o.label}</option>)}
          </select>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setOrderDir(orderDir === "desc" ? "asc" : "desc")}
            title={orderDir === "desc" ? "Mayor a menor" : "Menor a mayor"}
          >
            <i className={`mdi mdi-sort-${orderDir === "desc" ? "descending" : "ascending"}`} />
          </button>
          <span style={{ fontSize: 12, color: "#64748b" }}>
            Pág. {page + 1} / {totalPages}
          </span>
        </div>
      </div>

      {error && (
        <div style={{ padding: "10px 14px", background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: 8, marginBottom: 12 }}>
          <i className="mdi mdi-alert-circle me-1" />{error}
        </div>
      )}

      <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
        <div style={{ maxHeight: 640, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ ...cellHead, width: 60, textAlign: "center" }}>Rank</th>
                <th style={cellHead}>Producto</th>
                <th style={{ ...cellHead, textAlign: "center" }}>ABC</th>
                <th style={{ ...cellHead, textAlign: "right" }}>Importe</th>
                <th style={{ ...cellHead, textAlign: "right" }}>Cantidad</th>
                <th style={{ ...cellHead, textAlign: "center" }}>Rotación</th>
                <th style={{ ...cellHead, textAlign: "center" }}>Tendencia</th>
                <th style={{ ...cellHead, textAlign: "right" }}>Penetración</th>
                <th style={{ ...cellHead, textAlign: "right" }}>Sucursales</th>
                <th style={{ ...cellHead, textAlign: "right" }}>Clientes</th>
                <th style={{ ...cellHead }}>Alertas</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={11} style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
                  <Spinner color="primary" /> <div className="mt-2">Cargando…</div>
                </td></tr>
              )}
              {!loading && filtrados.length === 0 && (
                <tr><td colSpan={11} style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
                  Sin productos para los filtros aplicados.
                </td></tr>
              )}
              {!loading && filtrados.map(p => (
                <FilaProducto key={p.id_producto} producto={p} onVerDetalle={onVerDetalle} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setPage(0)} disabled={page === 0 || loading}>
            <i className="mdi mdi-page-first" />
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0 || loading}>
            <i className="mdi mdi-chevron-left" /> Anterior
          </button>
          <span style={{ fontSize: 13, color: "#64748b" }}>
            Mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} de {formatNumber(total)}
          </span>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page + 1 >= totalPages || loading}>
            Siguiente <i className="mdi mdi-chevron-right" />
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setPage(totalPages - 1)} disabled={page + 1 >= totalPages || loading}>
            <i className="mdi mdi-page-last" />
          </button>
        </div>
      )}
    </div>
  )
}

ProductosTab.propTypes = { filtros: PropTypes.object, onVerDetalle: PropTypes.func.isRequired }

export default ProductosTab
